/**
 * Prix d'une course OPÉRÉE — UNE seule règle, côté TypeScript.
 *
 * Jumeau exact de la fonction SQL `public._calculer_prix_operee()` (migrations 0030 puis 0043) et des distances
 * `distance_route_km()` (0030) et `distance_route_repli_km()` (0066). La BASE est autoritative : un déclencheur sur `courses` recalcule le prix à
 * l'insertion et ignore ce que le client envoie. Ce fichier sert à l'APERÇU en direct de la console (le
 * prix annoncé au téléphone avant « Créer la course ») et à l'écran de réglage de la grille. Les MÊMES
 * vecteurs de test tournent côté SQL (supabase/tests/operee/10_scenarios_prix.sql et
 * 30_scenarios_progressif_tension.sql) et côté TypeScript (web/operateur-ts/src/api/prixOpere.test.ts) :
 * si les deux divergent, un test casse. Ne JAMAIS recoder ce calcul ailleurs.
 *
 * Fichier SANS dépendance, sans API Deno ni DOM : importé tel quel par Vite (console opérateur,
 * extension omise) et, le cas échéant, par Deno. `.vercelignore` doit le ré-inclure (voir le
 * commentaire de ce fichier) : sans cela, le build Vercel échoue faute de fichier.
 *
 * La règle (décision du 06/10/2026, barème PROGRESSIF depuis le 12/10/2026 — migration 0043) :
 *   distance      = depuis le 20/10/2026 (0066) : la ROUTE à moto calculée par le service d'itinéraires du serveur et
 *                   mémorisée en base ; sans elle, le secours = vol d'oiseau (haversine, R = 6 371 km) × 1,56, au
 *                   centième de km (`distanceRepliKm`). Avant : vol d'oiseau × 1,3 (`distanceRouteKm`) ;
 *   points        = (0 km, prix minimum) puis, pour chaque tranche, (jusqu_a_km, prix_fcfa) : le prix
 *                   d'une tranche est le prix À SA LIMITE HAUTE ;
 *   prix de base  = interpolation LINÉAIRE entre les deux points qui encadrent min(distance, seuil)
 *                   (au-delà du dernier point : le prix du dernier point), avec un PLANCHER au prix minimum ;
 *   prix course   = arrondi( prix de base × coefficient du véhicule + km_sup × prix_km ) ;
 *   km_sup        = km ENTAMÉS au-delà du seuil de longue distance (15,01 km → 1 km) ;
 *   prix_km       = tarif réduit si le client a l'option mensuelle, sinon tarif normal ;
 *                   le coefficient du véhicule ne s'applique PAS au km supplémentaire ;
 *   arrondi       = multiple de `arrondi_fcfa` le plus proche, demi vers le haut ;
 *   majoration    = forte demande dans la zone d'enlèvement (voir `pourcentTension`), en % du prix
 *                   course, arrondie comme lui ; JAMAIS pour un client couvert par un forfait ;
 *   total         = prix course + majoration + supplément hors créneau ;
 *   véhicule non précisé = moto (le barème de référence).
 * Tout est calculé en ENTIERS (centièmes de km, centièmes de coefficient, BigInt pour les produits) :
 * aucune erreur de flottant sur l'argent, et le résultat est identique à celui de Postgres.
 */

/* ------------------------------------------------------------------ constantes */

/** Facteur de route historique : vol d'oiseau × 1,3. Même valeur que `distance_route_km()` en SQL (0030). */
export const FACTEUR_ROUTE = 1.3
/**
 * Facteur de SECOURS, sans route calculée : vol d'oiseau × 1,56 (= 1,3 × 1,2), calibré le 20/10/2026 sur 300 trajets à moto
 * dans Bamako pour garder le prix moyen avec le barème recalé. Même valeur que `distance_route_repli_km()` en SQL (0066).
 */
export const FACTEUR_REPLI = 1.56
/** Rayon moyen de la Terre, en km. Même valeur que `distance_vol_oiseau_km()` en SQL. */
export const RAYON_TERRE_KM = 6371

export type TypeVehicule = 'moto' | 'velo' | 'tricycle' | 'voiture' | 'pickup' | 'van' | 'camion' | 'autre'

/** Les huit valeurs de `public.vehicle_type` (0001 + 0025), dans l'ordre d'affichage. */
export const VEHICULES: readonly TypeVehicule[] = ['moto', 'velo', 'tricycle', 'voiture', 'pickup', 'van', 'camion', 'autre']

export const LIBELLE_VEHICULE: Record<TypeVehicule, string> = {
  moto: 'Moto',
  velo: 'Vélo',
  tricycle: 'Tricycle',
  voiture: 'Voiture',
  pickup: 'Pick-up',
  van: 'Fourgonnette',
  camion: 'Camion',
  autre: 'Autre',
}

/* ------------------------------------------------------------------ la grille */

export interface Tranche {
  /** Borne haute INCLUSE de la tranche, en km. */
  jusqu_a_km: number
  /** Prix À `jusqu_a_km` (barème progressif, 0043) : entre deux points, le prix est interpolé. */
  prix_fcfa: number
}

/** Les colonnes de `tarifs_operes` qui décident du prix. */
export interface GrilleOperee {
  tranches: Tranche[]
  coefficients: Record<TypeVehicule, number>
  seuil_longue_distance_km: number
  prix_km_sup_fcfa: number
  prix_km_sup_option_fcfa: number
  /** Supplément hors créneau de la grille — celui de la fiche client prime quand il est donné. */
  supplement_hors_creneau_fcfa: number
  arrondi_fcfa: number
  /** Prix à 0 km, premier point du barème progressif, et PLANCHER du prix de base (0043). */
  prix_minimum_fcfa: number
  /* Majoration de tension (forte demande dans la zone d'enlèvement, 0043). */
  /** Désactivée par défaut. */
  tension_active: boolean
  /** Rayon autour du point d'enlèvement, en km à vol d'oiseau. */
  tension_rayon_km: number
  /** Ratio demande / offre à partir duquel la majoration commence. */
  tension_seuil: number
  /** Points de pourcentage de majoration par point de ratio au-delà du seuil. */
  tension_pente_pct: number
  /** Majoration maximale, en %. */
  tension_plafond_pct: number
  /** Part de la majoration reversée au livreur, en %. */
  tension_part_livreur_pct: number
}

/**
 * Valeurs initiales PROVISOIRES : celles des colonnes par défaut de `tarifs_operes`. Des ordres de grandeur en attendant que le
 * propriétaire valide sa grille. Depuis le 20/10/2026 (0066) elles suivent les distances RÉELLES : tranches à 3,5 / 7 / 12 / 18 km,
 * seuil 18 km, 250 / 125 F le km supplémentaire (prix moyen inchangé sur les trajets d'étalonnage).
 */
export const GRILLE_PAR_DEFAUT: GrilleOperee = {
  tranches: [
    { jusqu_a_km: 3.5, prix_fcfa: 1500 },
    { jusqu_a_km: 7, prix_fcfa: 2000 },
    { jusqu_a_km: 12, prix_fcfa: 2500 },
    { jusqu_a_km: 18, prix_fcfa: 3500 },
  ],
  coefficients: { moto: 1.0, velo: 0.8, tricycle: 1.5, voiture: 2.0, pickup: 2.5, van: 3.0, camion: 4.0, autre: 2.0 },
  seuil_longue_distance_km: 18,
  prix_km_sup_fcfa: 250,
  prix_km_sup_option_fcfa: 125,
  supplement_hors_creneau_fcfa: 500,
  arrondi_fcfa: 50,
  prix_minimum_fcfa: 1000,
  tension_active: false,
  tension_rayon_km: 3,
  tension_seuil: 1,
  tension_pente_pct: 20,
  tension_plafond_pct: 30,
  tension_part_livreur_pct: 80,
}

/** La grille par défaut du 06/10/2026 (distances estimées × 1,3) : celle des vecteurs de test partagés avec le SQL. */
export const GRILLE_2026_10_06: GrilleOperee = {
  ...GRILLE_PAR_DEFAUT,
  tranches: [
    { jusqu_a_km: 3, prix_fcfa: 1500 },
    { jusqu_a_km: 6, prix_fcfa: 2000 },
    { jusqu_a_km: 10, prix_fcfa: 2500 },
    { jusqu_a_km: 15, prix_fcfa: 3500 },
  ],
  seuil_longue_distance_km: 15,
  prix_km_sup_fcfa: 300,
  prix_km_sup_option_fcfa: 150,
}

/** Fraîcheur maximale de la position d'un livreur pour qu'il compte dans l'offre (même valeur qu'en SQL). */
export const TENSION_FRAICHEUR_MIN = 5

/** Abonnement mensuel de l'option longue distance, par défaut (information de facturation — lot 2). */
export const OPTION_LONGUE_DISTANCE_MENSUEL_DEFAUT = 10000

/**
 * Problèmes d'une grille, en français (vide = valide). Miroir de la contrainte
 * `tarifs_operes_forme_chk` et des `check` de la table : la base refuse de toute façon, ceci
 * permet de le dire AVANT l'envoi, champ par champ.
 */
export function verifierGrille(g: GrilleOperee): string[] {
  const pb: string[] = []
  if (!Array.isArray(g.tranches) || g.tranches.length < 1 || g.tranches.length > 10) {
    pb.push('Le barème doit avoir entre 1 et 10 tranches.')
  } else {
    let prec = 0
    g.tranches.forEach((t, i) => {
      const n = i + 1
      if (!Number.isFinite(t.jusqu_a_km) || t.jusqu_a_km <= prec) {
        pb.push(`Tranche ${n} : la distance doit être strictement supérieure à celle de la tranche précédente.`)
      } else if (t.jusqu_a_km > 1000 || Math.abs(t.jusqu_a_km * 100 - Math.round(t.jusqu_a_km * 100)) > 1e-6) {
        pb.push(`Tranche ${n} : la distance est limitée à 1 000 km, deux décimales au plus.`)
      }
      if (Number.isFinite(t.jusqu_a_km) && t.jusqu_a_km > prec) prec = t.jusqu_a_km
      if (!Number.isInteger(t.prix_fcfa) || t.prix_fcfa < 0 || t.prix_fcfa > 10_000_000) {
        pb.push(`Tranche ${n} : le prix doit être un nombre entier de francs, positif ou nul.`)
      }
    })
  }
  for (const v of VEHICULES) {
    const c = g.coefficients?.[v]
    if (typeof c !== 'number' || !Number.isFinite(c) || c < 0.1 || c > 20 || Math.abs(c * 100 - Math.round(c * 100)) > 1e-6) {
      pb.push(`Coefficient ${LIBELLE_VEHICULE[v].toLowerCase()} : entre 0,1 et 20, deux décimales au plus.`)
    }
  }
  if (!(g.seuil_longue_distance_km > 0) || g.seuil_longue_distance_km > 9999.99) {
    pb.push('Le seuil de longue distance doit être supérieur à 0 km.')
  }
  for (const [nom, v] of [
    ['Prix du km supplémentaire', g.prix_km_sup_fcfa],
    ['Prix du km supplémentaire avec option', g.prix_km_sup_option_fcfa],
    ['Supplément hors créneau', g.supplement_hors_creneau_fcfa],
  ] as const) {
    if (!Number.isInteger(v) || v < 0) pb.push(`${nom} : un nombre entier de francs, positif ou nul.`)
  }
  if (!Number.isInteger(g.arrondi_fcfa) || g.arrondi_fcfa < 1 || g.arrondi_fcfa > 1000) {
    pb.push("L'arrondi doit être un entier entre 1 et 1 000 francs (1 = pas d'arrondi).")
  }

  // Barème progressif (0043) : prix minimum, puis un prix qui ne BAISSE jamais quand la distance augmente.
  // Miroir de `tarifs_operes_progression_valide()`.
  const min = g.prix_minimum_fcfa
  if (!Number.isInteger(min) || min < 0 || min > 10_000_000) {
    pb.push('Prix minimum : un nombre entier de francs, positif ou nul.')
  } else if (Array.isArray(g.tranches)) {
    let precPrix = min
    let precLib = 'le prix minimum (0 km)'
    g.tranches.forEach((t, i) => {
      if (Number.isInteger(t.prix_fcfa) && t.prix_fcfa >= 0) {
        if (t.prix_fcfa < precPrix) {
          pb.push(`Tranche ${i + 1} : le prix ne peut pas baisser quand la distance augmente (${t.prix_fcfa} F à ${t.jusqu_a_km} km, moins que ${precLib}).`)
        }
        precPrix = Math.max(precPrix, t.prix_fcfa)
        precLib = `${t.prix_fcfa} F à ${t.jusqu_a_km} km`
      }
    })
  }

  const deuxDecimales = (v: number) => Math.abs(v * 100 - Math.round(v * 100)) <= 1e-6
  const borne = (v: unknown, a: number, b: number, strictA = false) =>
    typeof v === 'number' && Number.isFinite(v) && (strictA ? v > a : v >= a) && v <= b && deuxDecimales(v)
  if (typeof g.tension_active !== 'boolean') pb.push('Majoration de tension : activée ou non.')
  if (!borne(g.tension_rayon_km, 0, 50, true)) pb.push('Rayon de la zone de tension : plus de 0 et au plus 50 km, deux décimales au plus.')
  if (!borne(g.tension_seuil, 0, 100)) pb.push('Seuil de tension : un ratio entre 0 et 100, deux décimales au plus.')
  if (!borne(g.tension_pente_pct, 0, 1000)) pb.push('Pente de la majoration : entre 0 et 1 000 % par point de ratio, deux décimales au plus.')
  if (!borne(g.tension_plafond_pct, 0, 100)) pb.push('Plafond de la majoration : entre 0 et 100 %, deux décimales au plus.')
  if (!borne(g.tension_part_livreur_pct, 0, 100)) pb.push('Part du livreur : entre 0 et 100 %, deux décimales au plus.')
  return pb
}

/* ------------------------------------------------------------------ distance */

const rad = (d: number): number => (d * Math.PI) / 180

/** Un point est-il exploitable ? `0,0` (golfe de Guinée) est un défaut d'appareil, pas une position. */
export function pointValide(lat: number | null | undefined, lng: number | null | undefined): boolean {
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return false
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false
  return !(lat === 0 && lng === 0)
}

/** Vol d'oiseau en km (haversine). Jumeau de `distance_vol_oiseau_km()`. */
export function distanceVolOiseauKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const sLat = Math.sin(rad(lat2 - lat1) / 2)
  const sLng = Math.sin(rad(lng2 - lng1) / 2)
  const a = sLat * sLat + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * sLng * sLng
  return 2 * RAYON_TERRE_KM * Math.asin(Math.min(1, Math.sqrt(a)))
}

/** Arrondi au centième, demi vers le haut, après réduction à 15 chiffres significatifs (comme le cast `double → numeric` de Postgres). */
function arrondiCentieme(x: number): number {
  return Math.round(Number(x.toPrecision(15)) * 100 + 1e-9) / 100
}

/** Distance « de route » estimée à l'ancienne : vol d'oiseau × 1,3, au centième de km. Jumeau de `distance_route_km()` (0030). */
export function distanceRouteKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  return arrondiCentieme(distanceVolOiseauKm(lat1, lng1, lat2, lng2) * FACTEUR_ROUTE)
}

/** Distance de SECOURS quand la route n'a pas pu être calculée : vol d'oiseau × 1,56. Jumeau de `distance_route_repli_km()` (0066). */
export function distanceRepliKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  return arrondiCentieme(distanceVolOiseauKm(lat1, lng1, lat2, lng2) * FACTEUR_REPLI)
}

/* ------------------------------------------------------------------ le prix */

/** Demande et offre comptées par la base autour du point d'enlèvement (`_tension_operee()`, 0043). */
export interface CompteTension {
  /** Courses en attente d'affectation dans le rayon, CELLE-CI COMPRISE (≥ 1). */
  demande: number
  /** Livreurs disponibles, position de moins de 5 min, dans le rayon. */
  offre: number
}

export interface EntreePrix {
  /** Distance en km : la route calculée par le serveur, sinon `distanceRepliKm`. */
  distance_km: number
  /** Véhicule demandé ; null/absent = moto. */
  vehicule?: TypeVehicule | null
  /** Le client a-t-il l'option mensuelle « longue distance » ? */
  option_longue_distance?: boolean
  /** La demande tombe-t-elle hors des créneaux du client ? */
  hors_creneau?: boolean
  /** Supplément de la FICHE client ; absent = celui de la grille. */
  supplement_client_fcfa?: number | null
  /** Tension au point d'enlèvement ; absent/null = non évaluée (aucune majoration). */
  tension?: CompteTension | null
  /** Le client est-il couvert par un forfait PME à la date de la course ? Alors jamais de majoration. */
  au_forfait?: boolean
}

/** Décomposition de la majoration de tension (clé `tension` du détail). */
export interface DetailTension {
  active: boolean
  /** Pourquoi aucune majoration n'a été calculée : grille désactivée, client au forfait, ou tension non évaluée. */
  exclue: 'desactivee' | 'forfait' | 'non_evaluee' | null
  demande: number | null
  offre: number | null
  /** demande / max(offre, 1), arrondi au centième (affichage). */
  ratio: number | null
  seuil: number
  pente_pct: number
  plafond_pct: number
  rayon_km: number
  part_livreur_pct: number
  /** Majoration retenue en %, au centième (tronquée : jamais en défaveur du client). */
  pct: number
}

/** Même forme que le jsonb rendu par `_calculer_prix_operee()` (sans la clé `source`, ajoutée par le déclencheur). */
export interface DetailPrix {
  distance_km: number
  /**
   * D'où vient la distance (0066) : `route` = la route à moto calculée par le serveur ; `estimation` = le secours (vol d'oiseau
   * × 1,56). Posé par la base dans le détail d'une course ; absent des courses d'avant la 0066 (vol d'oiseau × 1,3).
   */
  distance_source?: 'route' | 'estimation'
  vehicule: TypeVehicule
  coefficient: number
  /** Toujours `progressif` depuis la 0043 (absent des décompositions plus anciennes : barème « à marches »). */
  mode_bareme?: 'progressif'
  prix_minimum?: number
  /** Point bas de l'interpolation (0 km = prix minimum). */
  tranche_depuis_km?: number
  prix_depuis?: number
  /** Point haut de l'interpolation. */
  tranche_jusqu_a_km: number
  prix_jusqu?: number
  /** Le plancher (prix minimum) a-t-il relevé le prix interpolé ? */
  plancher?: boolean
  /** Prix de base AVANT coefficient, arrondi au franc pour l'affichage (le calcul garde la valeur exacte). */
  base: number
  /** base × coefficient, au centime. */
  base_vehicule: number
  seuil_longue_distance_km: number
  /** Km entamés au-delà du seuil. */
  longue_distance_km: number
  prix_km_sup: number
  longue_distance_fcfa: number
  option_longue_distance: boolean
  arrondi_fcfa: number
  /** Écart dû à l'arrondi (prix course − somme exacte au centime), positif ou négatif. */
  ajustement_arrondi: number
  /** Prix de la course arrondi, majoration et supplément exclus. */
  prix_course: number
  /** Décomposition de la tension (0043) ; absente des décompositions plus anciennes. */
  tension?: DetailTension
  /** « Forte demande : +X F » — 0 sans tension. */
  majoration_tension_fcfa?: number
  /** Part de la majoration reversée au livreur. */
  part_livreur_tension_fcfa?: number
  supplement_horaire: number
  total: number
}

export class ErreurPrixOpere extends Error {
  readonly code: 'distance_invalide' | 'grille_invalide'
  constructor(code: 'distance_invalide' | 'grille_invalide') {
    super(code)
    this.name = 'ErreurPrixOpere'
    this.code = code
  }
}

const centiemes = (x: number): number => Math.round(x * 100)
/** Division entière d'entiers POSITIFS (BigInt), comme `div()` de Postgres sur des positifs. */
const divB = (a: bigint, b: bigint): bigint => a / b

/**
 * Majoration de tension en CENTIÈMES de % : clamp((demande / max(offre, 1) − seuil) × pente, 0, plafond),
 * tronquée au centième de %. Jumeau de la partie « tension » de `_calculer_prix_operee()`.
 */
export function pourcentTensionCentiemes(g: GrilleOperee, t: CompteTension): number {
  const offre = BigInt(Math.max(Math.trunc(t.offre), 1))
  const demande = BigInt(Math.max(Math.trunc(t.demande), 0))
  const S = BigInt(centiemes(g.tension_seuil))
  const P = BigInt(centiemes(g.tension_pente_pct))
  const C = centiemes(g.tension_plafond_pct)
  const num = (demande * 100n - S * offre) * P
  if (num <= 0n) return 0
  return Math.min(Number(divB(num, 100n * offre)), C)
}

/** Le calcul. Pur et déterministe ; lève `ErreurPrixOpere` pour une distance négative ou non finie. */
export function calculerPrixOpere(grille: GrilleOperee, e: EntreePrix): DetailPrix {
  if (!Number.isFinite(e.distance_km) || e.distance_km < 0) throw new ErreurPrixOpere('distance_invalide')
  if (grille.tranches.length < 1) throw new ErreurPrixOpere('grille_invalide')

  const dist = arrondiCentieme(e.distance_km)
  const vehicule: TypeVehicule = e.vehicule ?? 'moto'
  const coefC = centiemes(grille.coefficients[vehicule]) // centièmes entiers
  const seuilC = centiemes(grille.seuil_longue_distance_km)
  const distC = centiemes(dist)
  const capC = Math.min(distC, seuilC)
  const min = grille.prix_minimum_fcfa ?? 0

  // Les deux points qui encadrent la distance (plafonnée au seuil) : (0 km, minimum) puis les tranches.
  let basKm = 0
  let basPrix = min
  let hautKm = 0
  let hautPrix = min
  let trouve = false
  for (const t of grille.tranches) {
    const kC = centiemes(t.jusqu_a_km)
    if (kC >= capC) {
      hautKm = kC
      hautPrix = t.prix_fcfa
      trouve = true
      break
    }
    basKm = kC
    basPrix = t.prix_fcfa
  }
  // Prix de base EXACT = num / den (francs). Au-delà du dernier point : le prix du dernier point.
  let num: bigint
  let den: bigint
  if (!trouve) {
    hautKm = basKm
    hautPrix = basPrix
    num = BigInt(basPrix)
    den = 1n
  } else {
    den = BigInt(hautKm - basKm)
    num = BigInt(basPrix) * den + BigInt(hautPrix - basPrix) * BigInt(capC - basKm)
  }
  const plancher = num < BigInt(min) * den
  if (plancher) num = BigInt(min) * den

  const option = !!e.option_longue_distance
  const kmSup = Math.floor((Math.max(0, distC - seuilC) + 99) / 100) // km entamés
  const prixKm = option ? grille.prix_km_sup_option_fcfa : grille.prix_km_sup_fcfa
  const longue = kmSup * prixKm
  const a = BigInt(grille.arrondi_fcfa)
  const C = BigInt(coefC)
  // Somme exacte en centimes = (num × coef_c + longue × 100 × den) / den.
  const brutNum = num * C + BigInt(longue) * 100n * den
  const course = Number(divB(brutNum + 50n * a * den, 100n * a * den) * a)
  const brutCentimes = Number(divB(2n * brutNum + den, 2n * den)) // au centime, demi vers le haut
  const base = Number(divB(2n * num + den, 2n * den))
  const baseVehiculeCentimes = Number(divB(2n * num * C + den, 2n * den))

  // Tension (0043).
  let tension: DetailTension
  let pctC = 0
  const commun = {
    seuil: grille.tension_seuil,
    pente_pct: grille.tension_pente_pct,
    plafond_pct: grille.tension_plafond_pct,
    rayon_km: grille.tension_rayon_km,
    part_livreur_pct: grille.tension_part_livreur_pct,
  }
  const t = e.tension ?? null
  const comptes = t
    ? { demande: Math.trunc(t.demande), offre: Math.trunc(t.offre), ratio: Number(divB(BigInt(Math.trunc(t.demande)) * 200n + BigInt(Math.max(Math.trunc(t.offre), 1)), 2n * BigInt(Math.max(Math.trunc(t.offre), 1)))) / 100 }
    : { demande: null, offre: null, ratio: null }
  if (!grille.tension_active) {
    tension = { active: false, exclue: 'desactivee', ...comptes, ...commun, pct: 0 }
  } else if (e.au_forfait) {
    tension = { active: true, exclue: 'forfait', ...comptes, ...commun, pct: 0 }
  } else if (!t) {
    tension = { active: true, exclue: 'non_evaluee', ...comptes, ...commun, pct: 0 }
  } else {
    pctC = pourcentTensionCentiemes(grille, t)
    tension = { active: true, exclue: null, ...comptes, ...commun, pct: pctC / 100 }
  }
  const majoration = pctC > 0 ? Number(divB(BigInt(course) * BigInt(pctC) + 5000n * a, 10000n * a) * a) : 0
  const partC = BigInt(centiemes(grille.tension_part_livreur_pct))
  const partLivreur = Number(divB(BigInt(majoration) * partC + 5000n, 10000n))

  const supplement = e.hors_creneau ? (e.supplement_client_fcfa ?? grille.supplement_hors_creneau_fcfa) : 0

  return {
    distance_km: dist,
    vehicule,
    coefficient: coefC / 100,
    mode_bareme: 'progressif',
    prix_minimum: min,
    tranche_depuis_km: basKm / 100,
    prix_depuis: basPrix,
    tranche_jusqu_a_km: hautKm / 100,
    prix_jusqu: hautPrix,
    plancher,
    base,
    base_vehicule: baseVehiculeCentimes / 100,
    seuil_longue_distance_km: grille.seuil_longue_distance_km,
    longue_distance_km: kmSup,
    prix_km_sup: prixKm,
    longue_distance_fcfa: longue,
    option_longue_distance: option,
    arrondi_fcfa: grille.arrondi_fcfa,
    ajustement_arrondi: (course * 100 - brutCentimes) / 100,
    prix_course: course,
    tension,
    majoration_tension_fcfa: majoration,
    part_livreur_tension_fcfa: partLivreur,
    supplement_horaire: supplement,
    total: course + majoration + supplement,
  }
}
